import { useState, useEffect, useRef } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { COMPANY_INFO } from "../data/companyData";

export default function Navbar() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [servicesDropdownOpen, setServicesDropdownOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(true);
  const dropdownRef = useRef(null);
  const location = useLocation();

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
    setServicesDropdownOpen(false);
  }, [location.pathname]);

  // Close desktop dropdown on outside click
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setServicesDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const serviceSubItems = [
    { label: "Oversigt over ydelser", href: "/vi-tilbyder", icon: "fa-solid fa-layer-group" },
    { label: "Nyt tag & tagrenovering", href: "/nyt-tag-ribe", icon: "fa-solid fa-house-chimney" },
    { label: "Nye vinduer & døre", href: "/nye-vinduer-og-doere-ribe", icon: "fa-solid fa-door-open" },
    { label: "Renovering af hus", href: "/renovering-af-hus", icon: "fa-solid fa-hammer" },
    { label: "Tilbygning & vinterhave", href: "/tilbygning-vinterhave", icon: "fa-solid fa-tree-city" },
    { label: "Snedkerarbejde & køkken", href: "/snedkerarbejde", icon: "fa-solid fa-ruler-combined" }
  ];

  const isServicesActive =
    location.pathname === "/vi-tilbyder" ||
    location.pathname === "/nyt-tag-ribe" ||
    location.pathname === "/nyt-tag" ||
    location.pathname === "/nye-vinduer-og-doere-ribe" ||
    location.pathname === "/vinduer-doere" ||
    location.pathname === "/renovering-af-hus" ||
    location.pathname === "/tilbygning-vinterhave" ||
    location.pathname === "/snedkerarbejde";

  return (
    <header className="sticky top-0 z-50 bg-white text-stone-800 shadow-sm border-b border-stone-200">
      {/* Top info strip */}
      <div className="bg-stone-100 text-stone-600 text-xs py-2 px-4 border-b border-stone-200">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <span className="hidden sm:inline-flex items-center text-stone-600">
              <i className="fa-solid fa-location-dot text-[#ce0611] mr-2" aria-hidden="true"></i>
              Tømrer i Ribe, Esbjerg, Bramming & omegn
            </span>
            <span className="inline-flex items-center text-stone-600">
              <i className="fa-solid fa-shield-check text-[#ce0611] mr-1.5" aria-hidden="true"></i>
              Solidt håndværk & faglig rådgivning
            </span>
          </div>
        </div>
      </div>

      {/* Main navigation container */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo & Brand */}
          <Link to="/" className="flex items-center space-x-3 group py-2">
            <img
              src={COMPANY_INFO.logoUrl}
              alt={COMPANY_INFO.name}
              className="h-11 sm:h-12 w-auto object-contain"
              onError={(e) => {
                e.target.style.display = "none";
                const fallback = document.getElementById("nav-logo-fallback");
                if (fallback) fallback.style.display = "flex";
              }}
            />
            <div id="nav-logo-fallback" className="hidden flex-col">
              <span className="font-bold text-base sm:text-lg tracking-tight text-stone-900 leading-tight">
                Tømrermester Søren Emil
              </span>
              <span className="text-xs text-[#ce0611] font-medium tracking-wide">
                v/ Jacob Heiselberg &bull; Ribe
              </span>
            </div>
          </Link>

          {/* Desktop Navigation Links (Visible on >= 1200px) */}
          <nav className="hidden min-[1200px]:flex items-center space-x-1" aria-label="Hovedmenu">
            <NavLink
              to="/"
              end
              className={({ isActive }) =>
                `px-3.5 py-2 rounded-full text-sm font-medium transition-colors ${
                  isActive
                    ? "text-[#ce0611] bg-red-50 font-semibold border border-red-200/60"
                    : "text-stone-700 hover:text-stone-900 hover:bg-stone-100"
                }`
              }
            >
              Forside
            </NavLink>

            {/* Services dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                type="button"
                onClick={() => setServicesDropdownOpen((prev) => !prev)}
                onMouseEnter={() => setServicesDropdownOpen(true)}
                className={`px-3.5 py-2 rounded-full text-sm font-medium transition-colors inline-flex items-center space-x-1.5 cursor-pointer ${
                  isServicesActive
                    ? "text-[#ce0611] bg-red-50 font-semibold border border-red-200/60"
                    : "text-stone-700 hover:text-stone-900 hover:bg-stone-100"
                }`}
                aria-expanded={servicesDropdownOpen}
                aria-haspopup="true"
              >
                <span>Vi tilbyder</span>
                <i
                  className={`fa-solid fa-chevron-down text-xs transition-transform duration-200 ${
                    servicesDropdownOpen ? "rotate-180 text-[#ce0611]" : "text-stone-500"
                  }`}
                  aria-hidden="true"
                ></i>
              </button>

              {servicesDropdownOpen && (
                <div
                  onMouseLeave={() => setServicesDropdownOpen(false)}
                  className="absolute left-0 mt-2 w-72 rounded-2xl bg-white border border-stone-200 shadow-xl py-2 z-50 animate-in fade-in slide-in-from-top-2 duration-150"
                >
                  <div className="px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-stone-500 border-b border-stone-100">
                    Håndværksydelser
                  </div>
                  {serviceSubItems.map((subItem) => (
                    <NavLink
                      key={subItem.href}
                      to={subItem.href}
                      className={({ isActive }) =>
                        `flex items-center px-4 py-2.5 text-sm transition-colors ${
                          isActive
                            ? "bg-red-50 text-[#ce0611] font-medium"
                            : "text-stone-700 hover:bg-stone-50 hover:text-stone-900"
                        }`
                      }
                      onClick={() => setServicesDropdownOpen(false)}
                    >
                      <i className={`${subItem.icon} w-5 text-[#ce0611] mr-2.5 text-center text-xs`} aria-hidden="true"></i>
                      <span>{subItem.label}</span>
                    </NavLink>
                  ))}
                </div>
              )}
            </div>

            <NavLink
              to="/galleri"
              className={({ isActive }) =>
                `px-3.5 py-2 rounded-full text-sm font-medium transition-colors ${
                  isActive
                    ? "text-[#ce0611] bg-red-50 font-semibold border border-red-200/60"
                    : "text-stone-700 hover:text-stone-900 hover:bg-stone-100"
                }`
              }
            >
              Galleri
            </NavLink>

            <NavLink
              to="/csr"
              className={({ isActive }) =>
                `px-3.5 py-2 rounded-full text-sm font-medium transition-colors ${
                  isActive
                    ? "text-[#ce0611] bg-red-50 font-semibold border border-red-200/60"
                    : "text-stone-700 hover:text-stone-900 hover:bg-stone-100"
                }`
              }
            >
              CSR & Vi støtter
            </NavLink>

            <NavLink
              to="/kontakt"
              className={({ isActive }) =>
                `px-3.5 py-2 rounded-full text-sm font-medium transition-colors ${
                  isActive
                    ? "text-[#ce0611] bg-red-50 font-semibold border border-red-200/60"
                    : "text-stone-700 hover:text-stone-900 hover:bg-stone-100"
                }`
              }
            >
              Kontakt
            </NavLink>
          </nav>

          {/* Desktop Right CTA Section (>= 1200px) */}
          <div className="hidden min-[1200px]:flex items-center space-x-4">
            <a
              href={`tel:${COMPANY_INFO.phoneRaw}`}
              className="flex items-center text-stone-700 hover:text-[#ce0611] text-sm font-medium transition-colors py-1.5 px-3 rounded-full hover:bg-red-50"
            >
              <i className="fa-solid fa-phone mr-2 text-[#ce0611]" aria-hidden="true"></i>
              <span className="whitespace-nowrap">{COMPANY_INFO.phone}</span>
            </a>
            <Link
              to="/kontakt"
              className="inline-flex items-center justify-center bg-[#ce0611] hover:bg-[#b0040e] text-white font-medium text-sm px-5 py-2.5 rounded-full shadow-sm transition-all hover:shadow hover:scale-[1.02] active:scale-[0.98]"
            >
              <i className="fa-solid fa-paper-plane mr-2 text-xs" aria-hidden="true"></i>
              <span>Få et tilbud</span>
            </Link>
          </div>

          {/* Mobile menu button (< 1200px strictly) */}
          <div className="flex min-[1200px]:hidden items-center space-x-3">
            <a
              href={`tel:${COMPANY_INFO.phoneRaw}`}
              className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-stone-100 border border-stone-200 text-stone-800 hover:text-[#ce0611] hover:bg-stone-200 transition-colors"
              aria-label="Ring til os"
            >
              <i className="fa-solid fa-phone" aria-hidden="true"></i>
            </a>

            <button
              type="button"
              onClick={() => setMobileMenuOpen((prev) => !prev)}
              className="inline-flex items-center justify-center p-2.5 rounded-full text-stone-700 hover:text-stone-900 hover:bg-stone-100 border border-stone-200 focus:outline-none focus:ring-2 focus:ring-[#ce0611]"
              aria-controls="mobile-menu"
              aria-expanded={mobileMenuOpen}
              aria-label="Åbn navigationsmenu"
            >
              {mobileMenuOpen ? (
                <i className="fa-solid fa-xmark text-xl" aria-hidden="true"></i>
              ) : (
                <i className="fa-solid fa-bars text-xl" aria-hidden="true"></i>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu dropdown (< 1200px) */}
      {mobileMenuOpen && (
        <div
          id="mobile-menu"
          className="min-[1200px]:hidden bg-white border-t border-stone-200 px-4 pt-3 pb-24 space-y-2 shadow-2xl animate-in slide-in-from-top duration-200 max-h-[calc(100vh-6.5rem)] max-h-[calc(100dvh-6.5rem)] overflow-y-auto overscroll-contain"
        >
          <NavLink
            to="/"
            end
            onClick={() => setMobileMenuOpen(false)}
            className={({ isActive }) =>
              `block px-3 py-2.5 rounded-full text-base font-medium transition-colors ${
                isActive ? "bg-red-50 text-[#ce0611] font-semibold" : "text-stone-700 hover:bg-stone-100"
              }`
            }
          >
            <i className="fa-solid fa-house mr-2.5 text-[#ce0611] text-sm" aria-hidden="true"></i>
            Forside
          </NavLink>

          {/* Collapsible Mobile Services */}
          <div>
            <button
              type="button"
              onClick={() => setMobileServicesOpen((prev) => !prev)}
              className={`w-full flex items-center justify-between px-3 py-2.5 rounded-full text-base font-medium transition-colors ${
                isServicesActive ? "bg-red-50 text-[#ce0611] font-semibold" : "text-stone-700 hover:bg-stone-100"
              }`}
            >
              <span className="flex items-center">
                <i className="fa-solid fa-layer-group mr-2.5 text-[#ce0611] text-sm" aria-hidden="true"></i>
                Vi tilbyder
              </span>
              <i
                className={`fa-solid fa-chevron-down text-xs transition-transform duration-200 ${
                  mobileServicesOpen ? "rotate-180" : ""
                }`}
                aria-hidden="true"
              ></i>
            </button>

            {mobileServicesOpen && (
              <div className="pl-4 pr-1 py-1 space-y-1 bg-stone-50 rounded-2xl mt-1 border border-stone-200">
                {serviceSubItems.map((sub) => (
                  <NavLink
                    key={sub.href}
                    to={sub.href}
                    onClick={() => setMobileMenuOpen(false)}
                    className={({ isActive }) =>
                      `flex items-center px-3 py-2 rounded-full text-sm transition-colors ${
                        isActive
                          ? "text-[#ce0611] font-medium bg-red-100/70"
                          : "text-stone-600 hover:text-stone-900 hover:bg-stone-100"
                      }`
                    }
                  >
                    <i className={`${sub.icon} w-5 text-[#ce0611] mr-2 text-xs text-center`} aria-hidden="true"></i>
                    <span>{sub.label}</span>
                  </NavLink>
                ))}
              </div>
            )}
          </div>

          <NavLink
            to="/galleri"
            onClick={() => setMobileMenuOpen(false)}
            className={({ isActive }) =>
              `block px-3 py-2.5 rounded-full text-base font-medium transition-colors ${
                isActive ? "bg-red-50 text-[#ce0611] font-semibold" : "text-stone-700 hover:bg-stone-100"
              }`
            }
          >
            <i className="fa-solid fa-images mr-2.5 text-[#ce0611] text-sm" aria-hidden="true"></i>
            Galleri
          </NavLink>

          <NavLink
            to="/csr"
            onClick={() => setMobileMenuOpen(false)}
            className={({ isActive }) =>
              `block px-3 py-2.5 rounded-full text-base font-medium transition-colors ${
                isActive ? "bg-red-50 text-[#ce0611] font-semibold" : "text-stone-700 hover:bg-stone-100"
              }`
            }
          >
            <i className="fa-solid fa-leaf mr-2.5 text-[#ce0611] text-sm" aria-hidden="true"></i>
            CSR & Vi støtter
          </NavLink>

          <NavLink
            to="/kontakt"
            onClick={() => setMobileMenuOpen(false)}
            className={({ isActive }) =>
              `block px-3 py-2.5 rounded-full text-base font-medium transition-colors ${
                isActive ? "bg-red-50 text-[#ce0611] font-semibold" : "text-stone-700 hover:bg-stone-100"
              }`
            }
          >
            <i className="fa-solid fa-envelope mr-2.5 text-[#ce0611] text-sm" aria-hidden="true"></i>
            Kontakt
          </NavLink>

          <div className="pt-4 border-t border-stone-200 space-y-3 pb-8">
            <a
              href={`tel:${COMPANY_INFO.phoneRaw}`}
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center justify-center w-full py-2.5 px-4 rounded-full bg-stone-100 text-stone-800 font-medium text-sm border border-stone-200 hover:bg-stone-200 transition-colors"
            >
              <i className="fa-solid fa-phone mr-2 text-[#ce0611]" aria-hidden="true"></i>
              <span className="whitespace-nowrap">Ring: {COMPANY_INFO.phone}</span>
            </a>
            <Link
              to="/kontakt"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center justify-center w-full py-2.5 px-4 rounded-full bg-[#ce0611] hover:bg-[#b0040e] text-white font-medium text-sm shadow transition-colors"
            >
              <i className="fa-solid fa-paper-plane mr-2 text-xs" aria-hidden="true"></i>
              <span>Få et tilbud</span>
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
